const STATUS_NO_ROWS = "no rows";
const STATUS_NO_COUNTER = "no counter data";
const STATUS_THIN = "thin sampling";

// Mirrors MIN_SAMPLES in api/utilities.py; only used to word the legend.
const MIN_SAMPLES = 5;

// "no rows" and "no counter data" need different people to fix them: one is a
// machine or network that went quiet, the other is a machine that is talking but
// whose counter channel is empty.
const UNKNOWN_STATES = {
    [STATUS_NO_ROWS]: {
        label: "Offline",
        colour: "danger",
        title: "Node sent nothing in this window, so production is unknown",
    },
    [STATUS_NO_COUNTER]: {
        label: "Sensor fault",
        colour: "warning",
        title: "Node reported, but its counter channel was empty",
    },
};

const FALLBACK_UNKNOWN = {
    label: "Unknown",
    colour: "secondary",
    title: "Production could not be determined for this window",
};

/**
 * A production figure, or an explicit state when there is no figure to show.
 *
 * Rendering 0 for a node that never reported is what makes an offline machine
 * look like an idle one, so an unknown reading is shown as a labelled state
 * instead of a number.
 */
const STATUS_STYLES = {
    ok: { label: "OK", colour: "success" },
    [STATUS_THIN]: { label: "Provisional", colour: "warning" },
    [STATUS_NO_COUNTER]: { label: "Sensor fault", colour: "warning" },
    [STATUS_NO_ROWS]: { label: "Offline", colour: "danger" },
};

// Keyed off STATUS_STYLES so the legend cannot drift from the badges it explains.
const STATUS_MEANINGS = {
    ok: "Full set of readings. Trust the count, including a zero.",
    [STATUS_THIN]: `Fewer than ${MIN_SAMPLES} readings, so the count may still rise.`,
    [STATUS_NO_COUNTER]: "Node is reporting, but its counter channel is empty.",
    [STATUS_NO_ROWS]: "Node sent nothing. Production unknown, not zero.",
};

/**
 * Explains the Data Status column.
 *
 * Laid out as a grid rather than a single wrapping row: four full sentences on
 * one line collapse into an unreadable band of text on a normal screen.
 */
export const DataStatusLegend = () => (
    <div className="card card-bordered mb-3">
        <div className="card-inner py-2">
            <h6 className="mb-2">Data Status key</h6>
            <div className="row g-2">
                {Object.entries(STATUS_STYLES).map(([status, style]) => (
                    <div key={status} className="col-sm-6 col-xxl-3">
                        <div className="d-flex align-items-start">
                            <span
                                className={`badge bg-${style.colour} me-2`}
                                style={{ minWidth: "92px" }}
                            >
                                {style.label}
                            </span>
                            <span className="text-soft">
                                {STATUS_MEANINGS[status]}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

/** The raw outcome of the last fetch, for diagnosis rather than for a tile. */
export const SyncStatusBadge = ({ row }) => {
    const status = row?.raw_data?.sync_status;
    if (!status) return <span>-</span>;
    const style = STATUS_STYLES[status] ?? {
        label: status,
        colour: "secondary",
    };
    return (
        <span
            className={`badge bg-${style.colour}`}
            title={STATUS_MEANINGS[status] ?? status}
        >
            {style.label}
        </span>
    );
};

const ProductionValue = ({ row, field }) => {
    const raw = row?.raw_data;
    const status = raw?.sync_status;

    if (raw && raw.production_known === false) {
        const state = UNKNOWN_STATES[status] ?? FALLBACK_UNKNOWN;
        return (
            <span
                className={`badge bg-${state.colour}`}
                title={state.title}
            >
                {state.label}
            </span>
        );
    }

    const value = raw?.[field];

    if (status === STATUS_THIN) {
        return (
            <span
                title={`Built from only ${raw?.sync_samples ?? "a few"} readings`}
            >
                {value}{" "}
                <span className="badge bg-warning">provisional</span>
            </span>
        );
    }

    return <span>{value}</span>;
};

export default ProductionValue;
